import { Auth0Client, BaseLoginOptions } from '@auth0/auth0-spa-js';
import asEnum from '~app/helpers/asEnum';
import { Auth0Config } from '~app/helpers/auth0Config';
import { stashEp, stashSm, stashSmAllow } from '~app/helpers/sessionStorage';
import getQueryParam, { hasQueryParam, getAllQueryParams } from '../helpers/queryParams';
import { AppState, SmApp, SmShow } from './handlers';

function getRedirectUri(): string {
  return `https://${window.location.hostname}/login/callback`;
}

const FORCE_AUTH: BaseLoginOptions['prompt'] = 'login';

function getSmShow(): SmShow | undefined {
  const showParam: SmShow | undefined = getQueryParam('sm.show');
  if (showParam !== undefined) {
    return showParam;
  }

  const { pathname } = window.location;
  if (pathname.includes('sign-up')) {
    return SmShow.SIGNUP;
  }
  if (pathname.includes('forgot-password')) {
    return SmShow.FORGOT_PASSWORD;
  }

  return undefined;
}

function getSmApp(): SmApp | undefined {
  const value = getQueryParam('sm.app');

  try {
    return asEnum(SmApp, value);
  } catch (e: unknown) {
    // value not in enum
  }
  return undefined;
}

function getSmForceAccontPicker(): true | undefined {
  if (hasQueryParam('sm.force_account_picker')) {
    return true;
  }
  return undefined;
}

/**
 * @returns the params that will be persisted with the login transaction
 */
function buildAppState(): AppState {
  return {
    show: getSmShow(),
    app: getSmApp(),
    forceAccountPicker: getSmForceAccontPicker(),
    ut_source: getQueryParam('ut_source'),
    ut_source2: getQueryParam('ut_source2'),
  };
}

/**
 * Begins a login transaction, storing the per-transaction params in
 * sessionStorage. Then navigates to the authentication provider.
 * Handles both sign-ups and logins, given the user can switch intent
 * on the login page.
 *
 * @param auth0Config Auth0 configuration
 * @param ep Optional URI where the user should be sent after completing
 *   the login transaction
 * @param language
 * @param country
 */
export default function login({
  auth0Config,
  ep,
  language,
  country,
  smAllowCreateUserCookieVal,
}: {
  auth0Config: Auth0Config;
  ep?: string;
  language?: string;
  country?: string;
  smAllowCreateUserCookieVal?: string;
}): void {
  const { domain, clientId: client_id, now } = auth0Config;
  if (!domain || !client_id) {
    throw new Error(`Missing auth0 configuration`);
  }

  const client = new Auth0Client({
    client_id,
    domain,
    redirect_uri: getRedirectUri(),
    nowProvider: () => now,
  });

  const sm = getQueryParam('sm');
  const smAllowCreateUser = smAllowCreateUserCookieVal ?? getQueryParam('sm_allow_create_user');
  const queryObj = getAllQueryParams();

  if (ep !== undefined) {
    stashEp(ep);
  }
  if (sm !== undefined) {
    stashSm(sm);
  }
  if (smAllowCreateUser !== undefined) {
    stashSmAllow(smAllowCreateUser);
  }

  const appState = buildAppState();
  const lang = language ?? 'en';

  // navigates away
  client
    .loginWithRedirect({
      ...queryObj, // propagate all our query params over to Auth0 (incl. 'ep' if we have it)

      appState,
      'sm.app': appState.app,
      'sm.show': appState.show,
      'sm.locale': lang,
      'sm.country': country,
      prompt: FORCE_AUTH, // SM auth cookie will redirect logged-in users; always prompt for Auth0 login
    })
    ?.catch(e => {
      // eslint-disable-next-line no-console
      console.error('Failed to login with redirect', e);
    });
}
